/**
 * DataManagerProjectile.ts
 *
 * Runtime manager for projectile data.
 * Merges:
 * - projectiles-c3data.json (base data from C3 extraction)
 * - projectiles-overrides.json (gameplay overrides)
 *
 * This follows the same c3data + overrides pattern as entities.
 */

import { ProjectileEffects } from '../systems/EffectsSystem';

// Re-export effect types
export type { ProjectileEffects };

// ============================================================================
// Projectile Types (moved here to avoid circular dependencies)
// ============================================================================

export type AttackType = 'normal' | 'explosive' | 'ice' | 'sonic';
export type ProjectileBehavior = 'bullet' | 'homing' | 'gravity' | 'static' | 'delayedLock';
export type CollisionType = 'aabb' | 'line' | 'circle';

export interface ProjectileDefinition {
  id: string;
  texture: string;
  width?: number;
  height?: number;
  originX?: number;
  originY?: number;
  animations?: Array<{
    name: string;
    frameCount: number;
    speed: number;
    isLooping: boolean;
  }>;
  damage: number;
  attackType: AttackType;
  speed: number;
  behavior: ProjectileBehavior;
  autoDestroy: boolean;
  destructOnFloor: boolean;
  abductable: boolean;
  duration: number;
  scale?: number;
  animation?: string;
  frame?: string;
  spawnOffset?: number;
  rotationSpeed?: number;
  homingTurnRate?: number;
  homingAcceleration?: number;
  spawnSound?: string;
  hitSound?: string;
  collisionType?: CollisionType;
  effects?: ProjectileEffects;
  aoeRadius?: number;
  aoeDamage?: number;
  aoeDuration?: number;
  aoeTint?: number;
  expansion?: {
    maxScaleX?: number;   // Max scale for X axis
    maxScaleY?: number;   // Max scale for Y axis
    maxScale?: number;    // Fallback for both (backwards compat)
    duration: number;
    axis?: 'x' | 'y' | 'both';  // Optional, can be removed
  };
  floorPenetration?: number;
  // Delayed lock behavior (orbs that wait, lock on target, then fire)
  delayedLockDelay?: number;    // ms to wait before locking (800)
  delayedLockSpeed?: number;    // speed after lock
  // Destructible projectiles (can be shot by player)
  destructible?: boolean;  // true = can be destroyed by player shots
  hp?: number;             // hit points
  // Death animation override (faster death animation)
  deathAnimFrameRate?: number;  // override frameRate for destroy animation
}

// ============================================================================
// C3 Data Types
// ============================================================================

export interface ProjectileC3Data {
  id: string;
  width: number;
  height: number;
  originX: number;
  originY: number;
  spriteFacingAngle: number;
  animations: Array<{
    name: string;
    frameCount: number;
    speed: number;
    isLooping: boolean;
  }>;
  imagePoints: Array<{
    name: string;
    x: number;
    y: number;
  }>;
  isInProyectilesFamily: boolean;
  behaviors: string[];
  instanceVariables: string[];
  hasCollisionPoly: boolean;
}

interface ProjectileC3DataFile {
  extractedAt: string;
  familyInstanceVariables: string[];
  familyBehaviors: string[];
  familyMembers: string[];
  projectiles: ProjectileC3Data[];
}

interface ProjectileOverridesFile {
  _meta?: {
    description: string;
    note: string;
  };
  projectiles: Record<string, Partial<ProjectileDefinition>>;
}

// ============================================================================
// DataManagerProjectile
// ============================================================================

/**
 * Static manager for projectile data.
 * Merges C3 extracted data with gameplay overrides at runtime.
 */
export class DataManagerProjectile {
  // C3 visual data (dimensions, origins, animations)
  private static c3Data: Map<string, ProjectileC3Data> = new Map();

  // Merged definitions (C3 + overrides)
  private static definitions: Map<string, ProjectileDefinition> = new Map();

  // Dynamic texture <-> ID mappings (built from definitions)
  private static textureToId: Map<string, string> = new Map();
  private static idToTexture: Map<string, string> = new Map();

  private static loaded: boolean = false;

  static readonly C3_JSON_KEY = 'projectiles-c3data';
  static readonly OVERRIDES_JSON_KEY = 'projectiles-overrides';

  /**
   * Preload JSON files (call in scene preload)
   */
  static preload(scene: Phaser.Scene): void {
    if (!scene.cache.json.exists(this.C3_JSON_KEY)) {
      scene.load.json(this.C3_JSON_KEY, '/assets/data/projectiles-c3data.json');
    }
    if (!scene.cache.json.exists(this.OVERRIDES_JSON_KEY)) {
      scene.load.json(this.OVERRIDES_JSON_KEY, '/assets/data/projectiles-overrides.json');
    }
  }

  /**
   * Load and merge data from cache (call in scene create)
   */
  static load(scene: Phaser.Scene): void {
    if (this.loaded) {
      return;
    }

    // Load C3 data
    const c3Json = scene.cache.json.get(this.C3_JSON_KEY) as ProjectileC3DataFile | undefined;
    if (!c3Json) {
      console.warn('[DataManagerProjectile] projectiles-c3data.json not loaded');
    }

    // Load overrides
    const overridesJson = scene.cache.json.get(this.OVERRIDES_JSON_KEY) as ProjectileOverridesFile | undefined;
    if (!overridesJson) {
      console.warn('[DataManagerProjectile] projectiles-overrides.json not loaded');
    }

    // Index C3 data by texture key
    if (c3Json?.projectiles) {
      for (const proj of c3Json.projectiles) {
        this.c3Data.set(proj.id, proj);
      }
    }

    // Build merged definitions
    if (overridesJson?.projectiles) {
      for (const [overrideId, override] of Object.entries(overridesJson.projectiles)) {
        // Texture comes from override.texture or defaults to overrideId
        const textureKey = override.texture ?? overrideId;
        const c3 = this.c3Data.get(textureKey);

        const merged: ProjectileDefinition = {
          // Base from C3
          id: overrideId,
          texture: textureKey,
          ...(c3 && {
            width: c3.width,
            height: c3.height,
            originX: c3.originX,
            originY: c3.originY,
            animations: c3.animations,
          }),

          // Defaults (required fields)
          damage: 1,
          attackType: 'normal' as AttackType,
          speed: 300,
          behavior: 'bullet' as ProjectileBehavior,
          autoDestroy: true,
          destructOnFloor: true,
          abductable: false,
          duration: 5000,

          // Override values
          ...override,
        };

        this.definitions.set(overrideId, merged);
      }
    }

    // Build texture <-> ID maps from merged definitions
    this.buildTextureMaps();

    this.loaded = true;
    // console.log(`[DataManagerProjectile] Loaded ${this.definitions.size} projectile definitions`);
  }

  /**
   * Build texture <-> ID maps from loaded definitions.
   * Called after definitions are merged.
   */
  private static buildTextureMaps(): void {
    this.textureToId.clear();
    this.idToTexture.clear();

    for (const [id, def] of this.definitions) {
      const texture = def.texture ?? id;
      this.textureToId.set(texture, id);
      this.idToTexture.set(id, texture);
    }
  }

  /**
   * Get merged projectile definition by ID
   */
  static get(id: string): ProjectileDefinition | undefined {
    return this.definitions.get(id);
  }

  /**
   * Get C3 visual data by texture key
   */
  static getC3Data(textureKey: string): ProjectileC3Data | undefined {
    return this.c3Data.get(textureKey);
  }

  /**
   * Get origin for a projectile
   */
  static getOrigin(textureKey: string): { x: number; y: number } {
    const c3 = this.c3Data.get(textureKey);
    return {
      x: c3?.originX ?? 0.5,
      y: c3?.originY ?? 0.5,
    };
  }

  /**
   * Check if definition exists
   */
  static has(id: string): boolean {
    return this.definitions.has(id);
  }

  /**
   * Get all projectile IDs
   */
  static getIds(): string[] {
    return Array.from(this.definitions.keys());
  }

  /**
   * Get friendly (player) projectile types
   */
  static getFriendlyTypes(): string[] {
    return ['laser', 'sonicray', 'plasmabomb', 'missile', 'mine'];
  }

  /**
   * Get enemy projectile types
   */
  static getEnemyTypes(): string[] {
    return ['bullet', 'rocket1', 'rocket2enemy', 'iceout', 'bolamagma', 'mutashot', 'escoba', 'bomb', 'scarecoworb'];
  }

  /**
   * Check if projectile is abductable
   */
  static isAbductable(id: string): boolean {
    return this.definitions.get(id)?.abductable ?? false;
  }

  /**
   * Check if data has been loaded
   */
  static isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Get projectile ID from texture key
   */
  static getIdFromTexture(texture: string): string | undefined {
    return this.textureToId.get(texture);
  }

  /**
   * Get texture key from projectile ID
   */
  static getTextureFromId(id: string): string | undefined {
    return this.idToTexture.get(id);
  }

  /**
   * Reset manager (for testing or scene restarts)
   */
  static reset(): void {
    this.c3Data.clear();
    this.definitions.clear();
    this.textureToId.clear();
    this.idToTexture.clear();
    this.loaded = false;
  }
}
